
import { endPoints } from "../../constants/endPoints";
import { makeApiCall } from "../../helpers/postRequest";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { api } from "../../api/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIError } from "../../constants/types";
import { IInternalLogEntry } from "../accessLogs/accessLogs.types";
import { GlobalMessagesConfig } from "../globalMessages/globalMessages.types";
import { ILead } from "../../models";

export const fetchLeadsList = createAsyncThunk<
  {
    data: ILead[];
    totalRecords: number;
  },
  {
    pageNumber: number;
    rowsInPerPage: number;
    limit: number;
    status: string;
    date: IDataTableV2DateState["dates"];
    searchValue: IDatatableV2AdvancedSearchFilter;
    isConvertStatus?: string;
  },
  { rejectValue: APIError }
>(
  "leads/fetchLeadsList",
  async (
    { pageNumber, rowsInPerPage, limit, status, date, searchValue },
    { rejectWithValue },
  ) => {
    try {
      let finalUrl = `${endPoints.leads.fetchleads}?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `${endPoints.leads.fetchleads}?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      const res = await api.get(finalUrl);
      const data: ILead[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      return {
        data,
        totalRecords,
      };
    } catch (err: any) {
      return rejectWithValue(err);
    }
  },
);

export const fetchLeads = createAsyncThunk<
  ILead,
  string,
  { rejectValue: APIError }
>("leads/fetchLeads", async (leadId: string, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.get(
      `${endPoints.leads.fetchleads}?value=${leadId}&column=${["lead_id"]}`,
    );
    const data = res.data.data;
    if (data.length > 0) {
      return data[0];
    } else {
      return rejectWithValue({
        message: "Unfortunately, there are no records available at the moment.",
        status: 404,
      });
    }
  } catch (err: any) {
    return rejectWithValue(err);
  }
});

export const upsertLead = createAsyncThunk<
  { lead: ILead },
  {
    lead: ILead;
    initialLead: ILead;
    isUpdate: boolean;
    isopportunity: boolean;
    logData?: IInternalLogEntry;
    globalMessagesConfig?: GlobalMessagesConfig;
  },
  { rejectValue: APIError }
>(
  "leads/upsertLead",
  async (
    payload: {
      lead: ILead;
      initialLead: ILead;
      isUpdate: boolean;
      isopportunity: boolean;
      globalMessagesConfig?: GlobalMessagesConfig;
    },
    { rejectWithValue },
  ) => {
    const { lead, initialLead, isUpdate, isopportunity } = payload;
    try {
      const res = await makeApiCall(
        {
          url: endPoints.leads.upsertLead,
          method: "POST",
          automation: {
            primaryFieldName: "lead_id",
            isUpdate: isUpdate,
          },
        },
        initialLead,
        lead,
      );
      if (isopportunity) {
        await api.post(endPoints.opportunity.createOpportunity, {
          first_name: lead.first_name,
          last_name: lead.last_name,
          email: lead.email,
          phone: lead.phone,
          lead_id: lead.lead_id,
          lead_source: lead.lead_source,
          insurance_type: lead.insurance_type,
          insurance_sub_type: lead.insurance_sub_type,
          notes: lead.notes,
          comment: lead.comment,
          time_to_contact: lead.time_to_contact,
          assigned_to_id: lead.assigned_to_id,
          due_amount: null,
          amount: null,
          type: null,
          source: lead.lead_source,
          specify: lead.specify,
          probability: null,
          business_name: lead.business_name,
          next_step: null,
          description: null,
          file_upload: null,
          status: lead.status,
          followup_type: lead.followup_type,
          follow_up_date: lead.followup_date,
          follow_reason: lead.followup_reason,
        });
      }
      return {
        lead: res.data.data as ILead,
      };
    } catch (err: any) {
      return rejectWithValue(err);
    }
  },
);

import { ILeadFollowUp } from "../../models";
import { ERROR_MESSAGES } from "../../constants/enums";

// ─── Follow-Up Thunks ────────────────────────────────────────────────────────

export const fetchLeadFollowUpsList = createAsyncThunk<
  {
    data: ILeadFollowUp[];
    totalRecords: number;
  },
  {
    pageNumber: number;
    rowsInPerPage: number;
    limit: number;
    status: string;
    date: IDataTableV2DateState["dates"];
    searchValue: IDatatableV2AdvancedSearchFilter;
  },
  { rejectValue: APIError }
>(
  "leads/fetchLeadFollowUpsList",
  async (
    { pageNumber, rowsInPerPage, limit, status, date, searchValue },
    { rejectWithValue },
  ) => {
    try {
      let finalUrl = `${endPoints.followUps.fetchFollowUps}?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `${endPoints.followUps.fetchFollowUps}?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      const res = await api.get(finalUrl);
      const data: ILeadFollowUp[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      return {
        data,
        totalRecords,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchLeadFollowUp = createAsyncThunk<
  ILeadFollowUp,
  string,
  { rejectValue: APIError }
>(
  "leads/fetchLeadFollowUp",
  async (followUpCode: string, { rejectWithValue }) => {
    try {
      const res = await api.get(`${endPoints.followUps.fetchFollowUps}?value=${followUpCode}&column=${["followup_code"]}`);
      const data: ILeadFollowUp[] = res.data.data;
      if (data.length > 0) {
        return data[0];
      } else {
        return rejectWithValue({
          message: "Unfortunately, there are no records available at the moment.",
          status: 404,
        });
      }
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const upsertLeadFollowUp = createAsyncThunk<
  { followUp: ILeadFollowUp },
  {
    followUp: ILeadFollowUp;
    initialFollowUp: ILeadFollowUp;
    isUpdate: boolean;
  },
  { rejectValue: APIError }
>(
  "leads/upsertLeadFollowUp",
  async (
    payload: {
      followUp: ILeadFollowUp;
      initialFollowUp: ILeadFollowUp;
      isUpdate: boolean;
    },
    { rejectWithValue },
  ) => {
    const { followUp, initialFollowUp, isUpdate } = payload;
    try {
      const res = await makeApiCall(
        {
          url: endPoints.followUps.upsertFollowUp,
          method: "POST",
          automation: {
            primaryFieldName: "follow_up_id",
            isUpdate: isUpdate,
          },
        },
        initialFollowUp,
        followUp,
      );

      return {
        followUp: res.data.data as ILeadFollowUp,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);
